import * as analysisState from '@/components/Analysis/state';
import { abyssColors } from '@/theme/colors';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Stack, SvgIcon, SxProps } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import { useAtom } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { PoiIcon } from '../../../modules/pointOfInterest/PointOfInterestTab/PoiIcon';
import { StyledCheckbox } from '../../PointsOfInterest/SummaryCard/styles';
import { accordionStyle, accordionSummaryStyle } from './styles';
import { ActionHeader } from '../../ActionHeader';
import { LocationTag } from '../../../../../constants';

type PoiTypeAccordionProps = {
  title: string;
  details: React.ReactNode;
  count: number;
  defaultExpanded: boolean;
  customStyle?: SxProps;
};

export const PoiTypeAccordion = ({
  title,
  details,
  count = 0,
  defaultExpanded = false,
  customStyle = {},
}: PoiTypeAccordionProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);
  const [selectedReviewPoiTypes, setSelectedReviewPoiTypes] = useAtom(
    analysisState.selectedReviewPoiTypes
  );

  const isTypeSelected = useMemo(
    () => selectedReviewPoiTypes.includes(title),
    [selectedReviewPoiTypes, title]
  );

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setSelectedReviewPoiTypes((prev) => [...prev, title]);
      } else {
        setSelectedReviewPoiTypes((prev) => prev.filter((item) => item !== title));
      }
    },
    [setSelectedReviewPoiTypes, title]
  );

  const handleChange = useCallback((event: React.SyntheticEvent, expanded: boolean) => {
    setIsExpanded(expanded);
  }, []);

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={isExpanded}
      onChange={handleChange}
      sx={{ ...accordionStyle, ...customStyle }}
      id={title}
      disabled={!count}
    >
      <AccordionSummary
        sx={accordionSummaryStyle}
        expandIcon={<ExpandMoreIcon sx={{ fontSize: '2rem' }} />}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flex={1}
          gap="0.8rem"
        >
          <Stack alignItems="center" direction="row" gap="0.8rem">
            <StyledCheckbox
              disableRipple
              checked={isTypeSelected}
              checkboxSize="md"
              onChange={handleCheckboxChange}
              onClick={(event) => event.stopPropagation()}
            />
            <SvgIcon sx={{ color: abyssColors.primary[300], fontSize: '1.6rem' }}>
              <PoiIcon name={title} />
            </SvgIcon>
            <Typography variant="caption" fontWeight={500}>
              {title}
            </Typography>
          </Stack>
          <Typography variant="body1" fontWeight={500} lineHeight="2.4rem">
            {count}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack>
          <ActionHeader variant="compact" poiType={title} location={LocationTag.ViewerPoiList} />
          {details}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
